import React, { useEffect, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import { Country, CreateCountryData } from "../../services/api";
import { useCreateCountry, useUpdateCountry } from "../../hooks/useCountries";

interface Props {
  country: Country | null;
  isOpen: boolean;
  onClose: () => void;
}

const emptyForm = (): CreateCountryData => ({
  name: "",
  currency: "",
  code: "",
  displayOrder: 0,
  isActive: true,
});

const CountryFormModal: React.FC<Props> = ({ country, isOpen, onClose }) => {
  const createMut = useCreateCountry();
  const updateMut = useUpdateCountry();

  const [form, setForm] = useState<CreateCountryData>(emptyForm());

  useEffect(() => {
    if (country) {
      setForm({
        name: country.name,
        currency: country.currency,
        code: country.code || "",
        displayOrder: country.displayOrder ?? 0,
        isActive: country.isActive,
      });
    } else {
      setForm(emptyForm());
    }
  }, [country, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Country name is required");
      return;
    }
    if (!/^[A-Za-z]{3}$/.test(form.currency.trim())) {
      toast.error("Currency must be a 3-letter ISO code (e.g. EUR, DKK)");
      return;
    }
    if (form.code && !/^[A-Za-z]{2}$/.test(form.code.trim())) {
      toast.error("Country code must be a 2-letter ISO code (e.g. DK)");
      return;
    }

    const payload: CreateCountryData = {
      name: form.name.trim(),
      currency: form.currency.trim().toUpperCase(),
      // DEVQA-13: undefined is dropped by JSON.stringify, so clearing the
      // input never reached the backend and the stored code stayed as-is.
      // null is serialized and the backend applies it ($set: {code: null}).
      code: form.code?.trim() ? form.code.trim().toUpperCase() : null,
      displayOrder: Number(form.displayOrder) || 0,
      isActive: form.isActive,
    };

    try {
      if (country) {
        await updateMut.mutateAsync({ id: country.id, data: payload });
      } else {
        await createMut.mutateAsync(payload);
      }
      onClose();
    } catch {
      // error toast handled by the hooks
    }
  };

  const saving = createMut.isPending || updateMut.isPending;
  const isEuro = form.currency.trim().toUpperCase() === "EUR";

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75"
          onClick={onClose}
        />

        <div className="inline-block w-full max-w-lg px-4 pt-5 pb-4 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 rounded-lg shadow-xl sm:p-6">
          <div className="flex items-start justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
              {country ? "Edit Country" : "New Country"}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Country Name
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, name: e.target.value }))
                }
                placeholder="e.g. Denmark"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Must match the name shown in the app's country picker.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Currency (ISO 4217)
                </label>
                <input
                  type="text"
                  maxLength={3}
                  value={form.currency}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      currency: e.target.value.toUpperCase(),
                    }))
                  }
                  placeholder="e.g. DKK"
                  className="w-full px-3 py-2 uppercase border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Country Code (ISO-2)
                </label>
                <input
                  type="text"
                  maxLength={2}
                  value={form.code || ""}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      code: e.target.value.toUpperCase(),
                    }))
                  }
                  placeholder="e.g. DK"
                  className="w-full px-3 py-2 uppercase border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>

            <div
              className={`text-xs px-3 py-2 rounded-lg ${
                isEuro
                  ? "bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
                  : "bg-amber-50 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
              }`}
            >
              {isEuro
                ? "EUR-market country — app shows EUR only (no currency picker)."
                : "Non-EUR country — app shows dual currency and offers a currency picker."}
            </div>

            <div className="grid grid-cols-2 gap-4 items-center">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Display Order
                </label>
                <input
                  type="number"
                  min={0}
                  value={form.displayOrder ?? 0}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      displayOrder: Number(e.target.value),
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <label className="flex items-center gap-2 mt-6 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!form.isActive}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, isActive: e.target.checked }))
                  }
                  className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Active (selectable in app)
                </span>
              </label>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50"
              >
                {saving
                  ? "Saving..."
                  : country
                    ? "Save Changes"
                    : "Create Country"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CountryFormModal;
